import { newWid } from "@/lib/builderOps";

// Item 32: build a tab out of an existing dashboard. Widgets are cloned with fresh ids
// (interaction / drill references remapped) so the tab behaves like any other tab:
// same engines, same filters, same exports — and can be re-synced from its source.
export function cloneWidgetsForTab(srcWidgets, tabId) {
  const list = (srcWidgets || []).filter((w) => w && w.id);
  const idMap = {};
  list.forEach((w) => { idMap[w.id] = newWid(); });
  const cloned = list.map((w) => ({
    ...w,
    id: idMap[w.id],
    parent_id: w.parent_id ? (idMap[w.parent_id] || null) : null,
    tab_id: tabId,
  }));
  // remap every remaining reference to a source widget id (interactions, drill targets…)
  let json = JSON.stringify(cloned);
  Object.entries(idMap).forEach(([oldId, newId]) => {
    json = json.split(`"${oldId}"`).join(`"${newId}"`);
  });
  return JSON.parse(json);
}

// Returns { tabs, widgets } with the source dashboard imported as a new (or refreshed) tab.
export function importDashboardAsTab({ tabs, widgets, source, tabId = null, name = null, cols = 12 }) {
  const srcWidgets = source.widgets || [];
  const tabName = (name || source.name || "Imported").slice(0, 60);
  let outTabs = [...tabs];
  let id = tabId;
  if (!id) {
    id = `t_${Math.random().toString(36).slice(2, 8)}`;
    outTabs.push({
      id, name: uniqueName(outTabs, tabName), order: outTabs.length, icon: "",
      icon_url: "", badge: { kind: "none", value: "", color: "" },
      hidden: false, disabled: false, locked: false, default: false,
      canvas: source.canvas && typeof source.canvas === "object" ? { ...source.canvas } : {},
      visibility: { mode: "everyone", user_emails: [], group_ids: [], roles: [], rules: [] },
      linked_dashboard_id: source.id, linked_name: source.name || "", linked_at: new Date().toISOString(),
    });
  } else {
    outTabs = outTabs.map((t) => (t.id === id
      ? { ...t, linked_dashboard_id: source.id, linked_name: source.name || "", linked_at: new Date().toISOString() }
      : t));
  }
  const kept = widgets.filter((w) => (w.tab_id || null) !== id);
  const cloned = cloneWidgetsForTab(srcWidgets, id);
  return { tabs: outTabs.map((t, i) => ({ ...t, order: i })), widgets: [...kept, ...cloned], tabId: id, count: cloned.length, cols };
}

function uniqueName(tabs, name) {
  const taken = new Set(tabs.map((t) => String(t.name || "").toLowerCase()));
  if (!taken.has(name.toLowerCase())) return name;
  for (let i = 2; i < 50; i += 1) {
    const n = `${name} ${i}`;
    if (!taken.has(n.toLowerCase())) return n;
  }
  return `${name} ${Date.now().toString(36)}`;
}
